import type { TFunction } from 'i18next'
import type { NavigateFunction } from 'react-router-dom'
import { ro, sl } from 'date-fns/locale'

import type {
    EmploymentMetaTypeDTO,
    EmploymentType,
    IndustryOfEmploymentDTO,
    SessionInfoDTO,
} from '@leanpay/models'
import type { Option } from '@leanpay/utils'
import { EmploymentContractType, Gender, sessionInfoSelectors, TwoFAStatus } from '@leanpay/models'
import {
    getCountryPhoneCodes,
    getIbanValidationOptions,
    Language,
    renderDecimalNumber,
} from '@leanpay/utils'

import i18n from '../i18n'
import {
    currentMarket,
    isMarketRom,
    isMarketSlo,
    isProdEnvironment,
    SelfCheckoutRoute,
} from './constants'

export const handleProgress = (route: string, setProgress: (progress: number) => void) => {
    if (isMarketRom) return handleSelfCheckoutProgress(route, setProgress)
}
const handleSelfCheckoutProgress = (route: string, setProgress: (progress: number) => void) => {
    switch (route.replace('/', '')) {
        case SelfCheckoutRoute.SplashScreen:
            setProgress(5)
            break
        case SelfCheckoutRoute.Intro:
            setProgress(10)
            break
        case SelfCheckoutRoute.Onboarding:
        case SelfCheckoutRoute.OnboardingShort:
            setProgress(15)
            break
        case SelfCheckoutRoute.PreQualifyIntro:
            setProgress(20)
            break
        case SelfCheckoutRoute.RequestOtp:
            setProgress(25)
            break
        case SelfCheckoutRoute.Login:
            setProgress(30)
            break
        case SelfCheckoutRoute.PersonalDetails:
            setProgress(35)
            break
        case SelfCheckoutRoute.TwoFaPin:
            setProgress(40)
            break
        case SelfCheckoutRoute.AddressDetails:
            setProgress(45)
            break
        case SelfCheckoutRoute.ValidateOfferPQ:
            setProgress(50)
            break
        case SelfCheckoutRoute.PreQualifySuccess:
            setProgress(55)
            break
        case SelfCheckoutRoute.SetPurchaseAmount:
            setProgress(60)
            break
        case SelfCheckoutRoute.ChoseInstallmentPlan:
            setProgress(65)
            break
        case SelfCheckoutRoute.ValidateOfferCheckout:
            setProgress(70)
            break
        case SelfCheckoutRoute.SigContract:
        case SelfCheckoutRoute.PepQuestionnaire:
        case SelfCheckoutRoute.ForgotTwoFaPin:
            setProgress(75)
            break
        case SelfCheckoutRoute.VerificationInfo:
            setProgress(80)
            break
        case SelfCheckoutRoute.KYCVerification:
            setProgress(85)
            break
        case SelfCheckoutRoute.KYCVerificationSuccess:
            setProgress(90)
            break
        case SelfCheckoutRoute.Payment:
            setProgress(95)
            break
        case SelfCheckoutRoute.PaymentStatus:
            setProgress(95)
            break
        case SelfCheckoutRoute.CheckoutSuccess:
            setProgress(100)
            break
        case SelfCheckoutRoute.PrequalifyError:
            setProgress(100)
            break
        case SelfCheckoutRoute.CheckoutError:
            setProgress(100)
            break
        default:
            setProgress(100)
            break
    }
}

export const shouldSetTwoFaPin = (sessionInfo?: SessionInfoDTO | null) => {
    if (!sessionInfo) return false

    return sessionInfo.twoFAStatus === TwoFAStatus.NOT_SET
}

export interface TwoFaPinLocationState {
    next?: string
}

// A returning customer without a PIN sets it before the step that login chose. The PIN
// screen then continues to that step.
export const navigateAfterLogin = (
    navigate: NavigateFunction,
    sessionInfo: SessionInfoDTO | undefined,
    route: string,
) => {
    if (sessionInfo && sessionInfoSelectors.isExistingMemberWithoutTwoFaPin(sessionInfo)) {
        const state: TwoFaPinLocationState = { next: route }
        navigate('/two-fa-pin', { replace: true, state })
        return
    }
    navigate(route, { replace: true })
}

export const getYesNoSelectOption = (translate: TFunction): Option[] => [
    {
        label: translate('financialDetails.labels.yes'),
        value: true,
    },
    {
        label: translate('financialDetails.labels.no'),
        value: false,
    },
]

export const getEmploymentTypesOptions = (
    translate: TFunction,
    employmentTypeData?: EmploymentMetaTypeDTO[],
): Option[] => {
    if (!employmentTypeData) return []
    return employmentTypeData.map((option) => ({
        label: translate(`enum.employmentType.${option.name}`) as string,
        value: option.name,
    }))
}

export const getEmploymentContractType = (
    translate: TFunction,
    selectedEmploymentType: EmploymentType | null,
    employmentTypeData?: EmploymentMetaTypeDTO[],
) => {
    if (!employmentTypeData || !selectedEmploymentType) return []

    //Selected employmentType type
    const filteredEmploymentType = employmentTypeData
        .filter(
            (type) =>
                !type.contractTypes.includes(EmploymentContractType.DO_NOT_WANT_TO_ASK) &&
                type.contractTypes.length > 1,
        )
        .find((employmentType) => employmentType.name === selectedEmploymentType)

    return (
        filteredEmploymentType?.contractTypes.map((type) => ({
            // @ts-expect-error - Dynamic translation key based on contract type
            label: translate(`enum.employmentContractType.${type}`) as string,
            value: type,
        })) ?? []
    )
}

export const countryPhoneCodes = getCountryPhoneCodes(currentMarket, isProdEnvironment)

export const getGenderOptions = (translate: TFunction) => {
    const options = [
        {
            value: Gender.Male,
            label: translate('enum.gender.MALE'),
        },
        {
            value: Gender.Female,
            label: translate('enum.gender.FEMALE'),
        },
    ]
    return options
}

export const formatBBAN = (input?: string): string => {
    if (!input) return ''
    return input
        .split('')
        .map((char, i) => ((i + 1) % 8 === 0 && i !== input.length - 1 ? `${char} - ` : char))
        .join('')
}

//TODO - use existing getter in session info
export const isExistingMembership = (sessionInfo: SessionInfoDTO | null) => {
    if (!sessionInfo) return false
    return (
        sessionInfo.membershipStatus !== 'NOT_SUBMITTED' &&
        sessionInfo.membershipStatus !== 'RESTARTED'
    )
}

export const getMonthLabel = (numberOfMonths?: number) => {
    if (!numberOfMonths) return 'months'
    if (isMarketSlo) {
        if (numberOfMonths === 3) {
            return 'month'
        }

        if (numberOfMonths !== 3) {
            return 'months'
        }
    }

    return 'months'
}

export const shouldShowProgressBar = () => {
    const progressBarExcludedPaths = [
        '/',
        '/onboarding',
        '/privacy-policy',
        '/terms-of-use',
        '/vendor-mock',
        '/complaint-policy',
        '/representative-example',
        '/financial-data-agreement',
        '/credit-bureau-agreement',
        '/cookie-policy',
        '/digital-verification-policy',
    ]

    return !progressBarExcludedPaths.includes(window.location.pathname)
}

export const isStandalonePublicRoute = [
    '/kyc-verification',
    '/payment-status',
    '/privacy-policy',
    '/complaint-policy',
    '/terms-of-use',
    '/representative-example',
    '/hardware-and-software-compatibility',
    '/financial-data-agreement',
    '/representative-example',
    '/cookie-policy',
    '/digital-verification-policy',
].includes(window.location.pathname)

export const ibanValidationOptions = getIbanValidationOptions(currentMarket)

export const getIndustryOfEmploymentOptions = (
    industriesOfEmployment?: IndustryOfEmploymentDTO[],
) =>
    industriesOfEmployment?.map((i) => ({
        value: i.id,
        label: i18n.language === Language.ENG ? i.nameEn : i.name,
    }))

export const currentLanguage = i18n.language

export const displayDecimalNumber = (value?: string | number) =>
    renderDecimalNumber(value, currentMarket)

export const getDateLocaleByLang = () => {
    const lang = i18n.language
    switch (lang) {
        case 'sl':
            return sl
        case 'ro':
            return ro
        default:
            return
    }
}

export enum KYC_DOCUMENT_TYPES {
    IDENTITY_CARD_FRONT_SIDE = 'IDENTITY_CARD_FRONT_SIDE',
    IDENTITY_CARD_BACK_SIDE = 'IDENTITY_CARD_BACK_SIDE',
    DRIVING_LICENCE_FRONT_SIDE = 'DRIVING_LICENCE_FRONT_SIDE',
    DRIVING_LICENCE_BACK_SIDE = 'DRIVING_LICENCE_BACK_SIDE',
    PASSPORT = 'PASSPORT',
    SELFIE = 'SELFIE',
    ADDRESS_CARD = 'ADDRESS_CARD',
}
export function mapOnfidoSdkResponseToDocumentIds(sdkResponse: any) {
    const temp: Record<string, string> = {}

    if (sdkResponse.face) {
        temp[KYC_DOCUMENT_TYPES.SELFIE] = sdkResponse.face.id
    }

    if (sdkResponse.document_front) {
        switch (sdkResponse.document_front.type) {
            case 'passport':
                temp[KYC_DOCUMENT_TYPES.PASSPORT] = sdkResponse.document_front.id
                break
            case 'driving_licence':
                temp[KYC_DOCUMENT_TYPES.DRIVING_LICENCE_FRONT_SIDE] = sdkResponse.document_front.id
                break
            case 'national_identity_card':
                temp[KYC_DOCUMENT_TYPES.IDENTITY_CARD_FRONT_SIDE] = sdkResponse.document_front.id
                break
        }
    }

    if (sdkResponse.document_back) {
        switch (sdkResponse.document_back.type) {
            case 'driving_licence':
                temp[KYC_DOCUMENT_TYPES.DRIVING_LICENCE_BACK_SIDE] = sdkResponse.document_back.id
                break
            case 'national_identity_card':
                temp[KYC_DOCUMENT_TYPES.IDENTITY_CARD_BACK_SIDE] = sdkResponse.document_back.id
                break
        }
    }

    // Now we cast temp to the desired type
    const documentIds = temp as { [key in keyof typeof KYC_DOCUMENT_TYPES]?: string }

    return Object.keys(documentIds).length > 0 ? documentIds : undefined
}
